import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Switch } from "../ui/switch";
import { Upload, Loader2, HelpCircle, Brain, ShieldAlert, Plus, X, GitBranch, BookOpen } from "lucide-react";
import { OcrValidationConfig } from "./OcrValidationConfig";

const normalizeTemplateForSteps = (template) => {
  if (!template) return null;
  return {
    ...template,
    sections: (template.sections || []).map((section) => ({
      ...section,
      rows: (section.rows || []).map((row, idx) => ({
        ...row,
        id: row.id || `${section.key}_r${String(idx + 1).padStart(2, "0")}`,
        source: row.source || "step_result",
        label: row.label || row.particulars || row.id || "",
      })),
    })),
  };
};

export const StepFormDialog = ({
  open,
  onOpenChange,
  editingStep,
  formData,
  setFormData,
  phases,
  cvTemplates,
  fileInputRef,
  submitting,
  onImageUpload,
  onPhaseChange,
  onSubmit,
  productInputFields = [],
  reportTemplate = null,
  token = null,
}) => {
  const [newExpKey, setNewExpKey] = useState("");
  const [newExpVal, setNewExpVal] = useState("");

  const [showRuleForm, setShowRuleForm] = useState(false);
  const [newRule, setNewRule] = useState({ when_field: "", when_value: "", pass_criteria: "", expected_values: {} });
  const [newRuleExpKey, setNewRuleExpKey] = useState("");
  const [newRuleExpVal, setNewRuleExpVal] = useState("");

  // Conditional Step — draft text per condition row for the "add value" input on free-text fields
  const [stepConditionValueDrafts, setStepConditionValueDrafts] = useState({});

  const addStepCondition = () => {
    const conditions = formData.step_condition?.conditions || [];
    setFormData({ ...formData, step_condition: { conditions: [...conditions, { when_field: "", when_values: [] }] } });
  };

  const removeStepCondition = (index) => {
    const conditions = (formData.step_condition?.conditions || []).filter((_, i) => i !== index);
    setFormData({ ...formData, step_condition: conditions.length ? { conditions } : null });
  };

  const updateStepConditionField = (index, fieldKey) => {
    const conditions = [...(formData.step_condition?.conditions || [])];
    conditions[index] = { when_field: fieldKey, when_values: [] };
    setFormData({ ...formData, step_condition: { conditions } });
  };

  const addStepConditionValue = (index, rawValue) => {
    const value = (rawValue || "").trim();
    if (!value) return;
    const conditions = [...(formData.step_condition?.conditions || [])];
    const existingValues = conditions[index]?.when_values || [];
    if (!existingValues.includes(value)) {
      conditions[index] = { ...conditions[index], when_values: [...existingValues, value] };
      setFormData({ ...formData, step_condition: { conditions } });
    }
  };

  const removeStepConditionValue = (index, value) => {
    const conditions = [...(formData.step_condition?.conditions || [])];
    conditions[index] = { ...conditions[index], when_values: (conditions[index]?.when_values || []).filter(v => v !== value) };
    setFormData({ ...formData, step_condition: { conditions } });
  };

  const addRuleExpectedValue = () => {
    const key = newRuleExpKey.trim();
    if (!key) return;
    const parsed = newRuleExpVal === "true" ? true : newRuleExpVal === "false" ? false : isNaN(newRuleExpVal) || newRuleExpVal === "" ? newRuleExpVal : Number(newRuleExpVal);
    setNewRule({ ...newRule, expected_values: { ...newRule.expected_values, [key]: parsed } });
    setNewRuleExpKey("");
    setNewRuleExpVal("");
  };

  const commitRule = () => {
    if (!newRule.when_field.trim() || !newRule.when_value.trim()) return;
    if (!newRule.pass_criteria.trim() && Object.keys(newRule.expected_values).length === 0) return;
    const rules = [...(formData.conditional_expected_values || []), { ...newRule }];
    setFormData({ ...formData, conditional_expected_values: rules });
    setNewRule({ when_field: "", when_value: "", pass_criteria: "", expected_values: {} });
    setNewRuleExpKey("");
    setNewRuleExpVal("");
    setShowRuleForm(false);
  };

  const canCommitRule = newRule.when_field.trim() && newRule.when_value.trim() &&
    (newRule.pass_criteria.trim() || Object.keys(newRule.expected_values).length > 0);

  const removeRule = (idx) => {
    const rules = (formData.conditional_expected_values || []).filter((_, i) => i !== idx);
    setFormData({ ...formData, conditional_expected_values: rules });
  };

  const addExpectedValue = () => {
    const key = newExpKey.trim();
    if (!key) return;
    const parsed = newExpVal === "true" ? true : newExpVal === "false" ? false : isNaN(newExpVal) || newExpVal === "" ? newExpVal : Number(newExpVal);
    setFormData({ ...formData, expected_values: { ...(formData.expected_values || {}), [key]: parsed } });
    setNewExpKey("");
    setNewExpVal("");
  };

  const removeExpectedValue = (key) => {
    const next = { ...(formData.expected_values || {}) };
    delete next[key];
    setFormData({ ...formData, expected_values: next });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh]" data-testid="step-form-dialog">
        <DialogHeader>
          <DialogTitle>{editingStep ? "Edit Step" : "Add New Step"}</DialogTitle>
          <DialogDescription>
            Define a verification step with reference image and criteria
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="step_number">Step Number</Label>
                <Input
                  id="step_number"
                  type="number"
                  min={1}
                  max={15}
                  value={formData.step_number}
                  onChange={(e) => setFormData({ ...formData, step_number: parseInt(e.target.value) })}
                  className="font-mono"
                  data-testid="step-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phase">Phase</Label>
                <Select
                  value={formData.phase_id}
                  onValueChange={onPhaseChange}
                >
                  <SelectTrigger id="phase" data-testid="step-phase-select">
                    <SelectValue placeholder="Select a phase" />
                  </SelectTrigger>
                  <SelectContent>
                    {phases.length > 0 ? (
                      phases.map((phase) => (
                        <SelectItem key={phase.id} value={phase.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: phase.color }}
                            />
                            {phase.name}
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="phase_1">Phase 1: Configuration</SelectItem>
                        <SelectItem value="phase_2">Phase 2: Wiring</SelectItem>
                        <SelectItem value="phase_3">Phase 3: Functional</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Step Name</Label>
              <Input
                id="name"
                placeholder="e.g., App-Physical Sync"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="step-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this step verifies..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="step-description-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="cv_task">CV/AI Task Type</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-sm">
                      <p className="font-medium mb-1">AI Task Templates</p>
                      <p className="text-xs text-muted-foreground">Select the computer vision task that best matches your verification requirement. Each task uses specialized AI prompts.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select
                value={formData.cv_task}
                onValueChange={(value) => setFormData({ ...formData, cv_task: value })}
              >
                <SelectTrigger id="cv_task" data-testid="step-cv-task-select">
                  <SelectValue placeholder="Select task type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(cvTemplates).length > 0 ? (
                    Object.entries(cvTemplates).map(([key, template]) => (
                      <SelectItem key={key} value={template.name}>
                        <div className="flex items-center gap-2">
                          <Brain className="w-3 h-3 text-blue-500" />
                          <span>{template.name}</span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="Classification">Classification</SelectItem>
                      <SelectItem value="Object Detection">Object Detection</SelectItem>
                      <SelectItem value="Anomaly Detection">Anomaly Detection</SelectItem>
                      <SelectItem value="Pattern Matching">Pattern Matching</SelectItem>
                      <SelectItem value="Instance Counting">Instance Counting</SelectItem>
                      <SelectItem value="Color Segmentation">Color Segmentation</SelectItem>
                      <SelectItem value="Keypoint Detection">Keypoint Detection</SelectItem>
                      <SelectItem value="Geometric Analysis">Geometric Analysis</SelectItem>
                      <SelectItem value="Luminance Analysis">Luminance Analysis</SelectItem>
                      <SelectItem value="OCR">OCR</SelectItem>
                      <SelectItem value="Temporal Analysis">Temporal Analysis</SelectItem>
                      <SelectItem value="API Call">API Call / Port Verification</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>

              {/* CV Template Info */}
              {formData.cv_task && cvTemplates && Object.values(cvTemplates).find(t => t.name === formData.cv_task) && (
                <div className="p-3 mt-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-blue-700">
                      {Object.values(cvTemplates).find(t => t.name === formData.cv_task)?.name}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {Object.values(cvTemplates).find(t => t.name === formData.cv_task)?.description}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    <strong>Example:</strong> {Object.values(cvTemplates).find(t => t.name === formData.cv_task)?.example}
                  </p>
                </div>
              )}

              {/* Gauge Config — shown only for Analog Gauge Reading */}
              {formData.cv_task === "Analog Gauge Reading" && (
                <div className="mt-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20 space-y-3">
                  <p className="text-xs font-semibold text-orange-700 flex items-center gap-1">
                    <Brain className="w-3 h-3" /> Gauge Comparison Mode
                  </p>

                  {/* Mode selector */}
                  <div className="flex gap-3">
                    {[
                      { value: "test_value", label: "Test-specific value", desc: "Tester enters expected reading; tolerance applied automatically" },
                      { value: "fixed_range", label: "Fixed range", desc: "Admin sets allowed min / max bar" },
                    ].map(({ value, label, desc }) => (
                      <label key={value} className="flex items-start gap-2 cursor-pointer flex-1">
                        <input
                          type="radio"
                          name="gauge_mode"
                          value={value}
                          checked={(formData.gauge_config?.mode ?? "test_value") === value}
                          onChange={() =>
                            setFormData({
                              ...formData,
                              gauge_config: {
                                ...(formData.gauge_config || {}),
                                mode: value,
                              },
                            })
                          }
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-xs font-medium">{label}</p>
                          <p className="text-xs text-muted-foreground">{desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Test-specific value: tolerance */}
                  {(formData.gauge_config?.mode ?? "test_value") === "test_value" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Tolerance (bar)</Label>
                      <p className="text-xs text-muted-foreground">
                        Allowed deviation from the tester-entered value. E.g. tester enters 6.9 → range becomes
                        {" "}{((formData.gauge_config?.tolerance ?? 0.5) * -1 + 6.9).toFixed(1)}–{((formData.gauge_config?.tolerance ?? 0.5) * 1 + 6.9).toFixed(1)} bar.
                      </p>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="5"
                        value={formData.gauge_config?.tolerance ?? 0.5}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            gauge_config: {
                              ...(formData.gauge_config || {}),
                              mode: "test_value",
                              tolerance: parseFloat(e.target.value) || 0.5,
                            },
                          })
                        }
                        className="h-8 w-32 text-sm"
                      />
                    </div>
                  )}

                  {/* Fixed range: min + max */}
                  {formData.gauge_config?.mode === "fixed_range" && (
                    <div className="flex gap-3 items-end">
                      <div className="space-y-1">
                        <Label className="text-xs">Min (bar)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={formData.gauge_config?.min_bar ?? ""}
                          placeholder="e.g. 6.5"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gauge_config: {
                                ...(formData.gauge_config || {}),
                                min_bar: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="h-8 w-28 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Max (bar)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={formData.gauge_config?.max_bar ?? ""}
                          placeholder="e.g. 7.5"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gauge_config: {
                                ...(formData.gauge_config || {}),
                                max_bar: parseFloat(e.target.value) || 7,
                              },
                            })
                          }
                          className="h-8 w-28 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pass_criteria">Pass Criteria</Label>
              <Input
                id="pass_criteria"
                placeholder="e.g., Sensor count matches app selection"
                value={formData.pass_criteria}
                onChange={(e) => setFormData({ ...formData, pass_criteria: e.target.value })}
                data-testid="step-pass-criteria-input"
              />
            </div>

            {/* Expected Values */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-purple-500" />
                Expected Values
                <span className="text-xs text-muted-foreground font-normal">(hints passed to AI)</span>
              </Label>
              {Object.entries(formData.expected_values || {}).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {Object.entries(formData.expected_values || {}).map(([k, v]) => (
                    <Badge key={k} variant="secondary" className="gap-1 font-mono text-xs">
                      {k}: {String(v)}
                      <button type="button" onClick={() => removeExpectedValue(k)} className="ml-1 hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="key (e.g. sensor_present)"
                  value={newExpKey}
                  onChange={(e) => setNewExpKey(e.target.value)}
                  className="font-mono text-sm h-8"
                  onKeyDown={(e) => e.key === "Enter" && addExpectedValue()}
                />
                <Input
                  placeholder="value (true / false / number / text)"
                  value={newExpVal}
                  onChange={(e) => setNewExpVal(e.target.value)}
                  className="font-mono text-sm h-8"
                  onKeyDown={(e) => e.key === "Enter" && addExpectedValue()}
                />
                <Button type="button" size="sm" variant="outline" onClick={addExpectedValue} className="h-8 px-2">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Use <code className="text-xs bg-muted px-1 rounded">true</code> / <code className="text-xs bg-muted px-1 rounded">false</code> for booleans, numbers for numeric checks, or text for string values.
              </p>
            </div>


            {/* QC Report Mappings */}
            {reportTemplate && (() => {
              const normTemplate = normalizeTemplateForSteps(reportTemplate);
              // Flatten mappable targets: normal source=step_result rows map 1:1, while a "configure
              // last column" grid expands into one virtual target per grid row whose last-column
              // cell is source=step_result — each grid row needs its own step mapping.
              const stepResultRows = (normTemplate.sections || []).flatMap(s =>
                (s.rows || []).flatMap(r => {
                  if (r.observationType === "grid" && r.grid_config?.configure_columns) {
                    const colHeaders = r.grid_config.col_headers || [];
                    const lastColIdx = colHeaders.length - 1;
                    const hdr = colHeaders[lastColIdx];
                    return (r.grid_config.rows || []).flatMap((gridRow, gi) => {
                      if (gridRow.cell_source !== "step_result") return [];
                      return [{
                        id: `${r.id}::c${lastColIdx}::r${gi}`,
                        row_id: r.id,
                        col_index: lastColIdx,
                        grid_row_index: gi,
                        label: `${r.label || r.id} — ${hdr || `Col ${lastColIdx + 1}`} (Row ${gi + 1})`,
                        section_key: s.key,
                        section_label: s.label || s.key,
                        observationType: gridRow.cell_type || "text",
                      }];
                    });
                  }
                  if (r.source === "step_result") {
                    return [{ ...r, row_id: r.id, col_index: undefined, grid_row_index: undefined, section_key: s.key, section_label: s.label || s.key }];
                  }
                  return [];
                })
              );
              if (stepResultRows.length === 0) return null;
              const mappings = formData.qc_mappings || [];
              const sameTarget = (a, b) => a.row_id === b.row_id && (a.col_index ?? null) === (b.col_index ?? null) && (a.grid_row_index ?? null) === (b.grid_row_index ?? null);
              const updateMapping = (idx, changes) => {
                const updated = mappings.map((m, i) => i === idx ? { ...m, ...changes } : m);
                setFormData({ ...formData, qc_mappings: updated });
              };
              const removeMapping = (idx) => {
                setFormData({ ...formData, qc_mappings: mappings.filter((_, i) => i !== idx) });
              };
              const addMapping = () => {
                setFormData({ ...formData, qc_mappings: [...mappings, { section_key: "", row_id: "", extraction_key: "", col_index: null, grid_row_index: null }] });
              };
              return (
                <div className="space-y-2 border rounded-lg p-3 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">QC Report Mappings</Label>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addMapping}>
                      <Plus className="w-3 h-3 mr-1" /> Add Row
                    </Button>
                  </div>
                  {mappings.length === 0 && (
                    <p className="text-xs text-muted-foreground">No mappings yet. Click Add Row to map this step to a QC report field.</p>
                  )}
                  {mappings.map((mapping, idx) => {
                    const selectedEntry = stepResultRows.find(r => sameTarget(r, mapping)) || null;
                    return (
                      <div key={idx} className="space-y-1.5 border rounded p-2 bg-background">
                        <div className="flex items-center gap-2">
                          <Select
                            value={selectedEntry?.id || "__none__"}
                            onValueChange={(val) => {
                              if (val === "__none__") { updateMapping(idx, { section_key: "", row_id: "", extraction_key: "", col_index: null, grid_row_index: null }); return; }
                              const entry = stepResultRows.find(r => r.id === val);
                              updateMapping(idx, { section_key: entry.section_key, row_id: entry.row_id, col_index: entry.col_index ?? null, grid_row_index: entry.grid_row_index ?? null, extraction_key: "" });
                            }}
                          >
                            <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select QC row…" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Select row —</SelectItem>
                              {stepResultRows.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.label || r.id} · {r.section_label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeMapping(idx)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        {selectedEntry?.observationType === "text" && (
                          <Input
                            className="h-7 text-xs font-mono"
                            placeholder="Extraction keys e.g. serial_number, model_name"
                            value={mapping.extraction_key || ""}
                            onChange={(e) => updateMapping(idx, { extraction_key: e.target.value })}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label>Reference Image</Label>
              <div
                className="aspect-video bg-muted rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.reference_image_base64 ? (
                  <img
                    src={`data:image/jpeg;base64,${formData.reference_image_base64}`}
                    alt="Reference"
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Click to upload reference image</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={onImageUpload}
                data-testid="step-image-upload"
              />
              {formData.reference_image_base64 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, reference_image_base64: null })}
                >
                  Remove image
                </Button>
              )}
            </div>

            {/* OCR Validation Config — uses the reference image uploaded above */}
            <OcrValidationConfig
              value={formData.ocr_validation}
              onChange={(v) => setFormData({ ...formData, ocr_validation: v })}
              token={token}
              referenceImageBase64={formData.reference_image_base64}
            />

            {/* Optional Step Toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/20">
              <Label className="text-sm font-semibold flex items-center gap-2 cursor-pointer">
                <BookOpen className="w-4 h-4 text-blue-500" />
                Optional Step
                <span className="text-xs font-normal text-muted-foreground">— failure won't fail the overall test</span>
              </Label>
              <Switch
                checked={!!formData.is_optional}
                onCheckedChange={(checked) => setFormData({ ...formData, is_optional: checked })}
              />
            </div>

            {/* Conditional Step Inclusion */}
            {productInputFields.length > 0 && (
              <div className="space-y-3 border rounded-lg p-3 bg-muted/20">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-orange-500" />
                    Conditional Step
                  </Label>
                  <Switch
                    checked={!!formData.step_condition}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, step_condition: checked ? { conditions: [{ when_field: "", when_values: [] }] } : null })
                    }
                  />
                </div>
                {formData.step_condition && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      This step is only added to a test when EVERY condition below is satisfied — each condition matches if the field's value is ANY of the listed values.
                    </p>
                    <div className="space-y-2">
                      {(formData.step_condition.conditions || []).map((cond, idx) => {
                        const field = productInputFields.find(f => f.key === cond.when_field);
                        const isSelectField = field?.type === "select" && field.options?.length > 0;
                        return (
                          <div key={idx} className="space-y-1.5 border rounded p-2 bg-background">
                            <div className="flex items-center gap-2">
                              <Select
                                value={cond.when_field}
                                onValueChange={(val) => updateStepConditionField(idx, val)}
                              >
                                <SelectTrigger className="h-8 text-xs flex-1">
                                  <SelectValue placeholder="Select field…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {productInputFields.map((f) => (
                                    <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeStepCondition(idx)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                            {cond.when_field && !field && (
                              <p className="text-xs text-destructive">
                                ⚠ Field "{cond.when_field}" no longer exists on this product type — pick a different field.
                              </p>
                            )}
                            {(cond.when_values || []).length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {cond.when_values.map((v) => (
                                  <Badge key={v} variant="secondary" className="gap-1 font-mono text-xs">
                                    {v}
                                    <button type="button" onClick={() => removeStepConditionValue(idx, v)} className="ml-1 hover:text-destructive">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {isSelectField ? (
                              <Select
                                value=""
                                onValueChange={(val) => addStepConditionValue(idx, val)}
                                disabled={!cond.when_field}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Add value…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {field.options.filter(opt => !(cond.when_values || []).includes(opt)).map((opt) => (
                                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="flex gap-2">
                                <Input
                                  className="h-8 text-xs"
                                  placeholder="Add value…"
                                  disabled={!cond.when_field}
                                  value={stepConditionValueDrafts[idx] || ""}
                                  onChange={(e) => setStepConditionValueDrafts({ ...stepConditionValueDrafts, [idx]: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      addStepConditionValue(idx, stepConditionValueDrafts[idx]);
                                      setStepConditionValueDrafts({ ...stepConditionValueDrafts, [idx]: "" });
                                    }
                                  }}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-2"
                                  disabled={!cond.when_field || !(stepConditionValueDrafts[idx] || "").trim()}
                                  onClick={() => {
                                    addStepConditionValue(idx, stepConditionValueDrafts[idx]);
                                    setStepConditionValueDrafts({ ...stepConditionValueDrafts, [idx]: "" });
                                  }}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addStepCondition}>
                        <Plus className="w-3 h-3 mr-1" /> Add condition
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={submitting} data-testid="save-step-btn">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editingStep ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
